import { AppNS } from 'app';
import { API_URL } from 'app/core/config';
import { AgencyCompany, AgencyStaff, ApiCollectionResponse, Params, ProjectInvitation } from 'app/types';

import { request } from '../config';
import { parseCompanyDocumentsData } from '../files/parsers';

const COMPANIES = 'companies';
const COMPANIES_URL = 'companies.json';
const INVITATION_URL = 'project_invitations.json';
const STAFF_URL = 'staff-members.json';
const INVITATION = 'project_invitations';
const DISCIPLINE = 'company-disciplines';

export function getAgencies(opts: AppNS.Params = {}, signal?: AbortSignal): Promise<AgencyCompany[]> {
  return request(
    {
      url: COMPANIES_URL,
      method: 'GET',
      params: {
        type: 'agency',
        isDeactivatedByAdmin: false,
        ...opts,
      },
      signal,
    },
    (data) => {
      return data.models.results;
    }
  );
}

export function apiGetCompanies(
  opts: AppNS.Params = {},
  signal?: AbortSignal
): Promise<ApiCollectionResponse<AppNS.Company>> {
  return request(
    {
      url: COMPANIES_URL,
      method: 'GET',
      params: {
        type: 'agency',
        limit: 20,
        ...opts,
      },
      signal,
    },
    (data) => {
      return data.models;
    }
  );
}

export function apiGetCompaniesPublicView(
  opts: AppNS.Params = {},
  signal?: AbortSignal
): Promise<ApiCollectionResponse<AppNS.Company>> {
  return request(
    {
      url: 'companies-public.json',
      method: 'GET',
      params: {
        limit: 20,
        ...opts,
      },
      signal,
    },
    (data) => {
      return data.models;
    }
  );
}

export function apiGetCompanyById(id: string, signal?: AbortSignal): Promise<AppNS.Company> {
  return request(
    {
      url: `${COMPANIES}/${id}.json`,
      method: 'GET',
      params: {
        with_merchant_account: true,
      },
      signal,
    },
    (data) => {
      return { ...data.model, documents: parseCompanyDocumentsData(data.model.documents) };
    }
  );
}

export function apiGetCompanyDisciplineById(
  id: string,
  companyId: string,
  signal?: AbortSignal
): Promise<AppNS.CompanyDiscipline> {
  return request(
    {
      url: `${DISCIPLINE}/${id}.json`,
      method: 'GET',
      signal,
      params: {
        companyId,
      },
    },
    (data) => data.model
  );
}

export function getAgencyInvitations(data: Params): Promise<ProjectInvitation[]> {
  return request(
    {
      url: INVITATION_URL,
      method: 'GET',
      params: { ...data, role: 'agency' },
    },
    (data) => {
      return data.models.results;
    }
  );
}

export function updateInvitation(id: string, action: 'reject' | 'accept'): Promise<ProjectInvitation> {
  if (action === 'reject') {
    return rejectInvitation(id);
  }
  return acceptInvitation(id);
}

export function acceptInvitation(id: string): Promise<ProjectInvitation> {
  return request(
    {
      url: `${INVITATION}/${id}/accept.json`,
      method: 'PATCH',
    },
    (data) => {
      return data.model;
    }
  );
}

export function rejectInvitation(id: string): Promise<ProjectInvitation> {
  return request(
    {
      url: `${INVITATION}/${id}/reject.json`,
      method: 'PATCH',
    },
    (data) => {
      return data.model;
    }
  );
}

export function getAgencyStaff(data: Params): Promise<AgencyStaff[]> {
  return request(
    {
      baseURL: API_URL, //old API
      url: STAFF_URL,
      method: 'GET',
      params: { ...data },
    },
    (data) => {
      return data.results;
    }
  );
}

export function apiGetAgencyEmployees(id: string): Promise<AppNS.Employment[]> {
  return request(
    {
      baseURL: API_URL, //old API
      url: `employees/${id}.json`,
      method: 'GET',
    },
    (data) => {
      return data.models;
    }
  );
}

export function setNewCompany(name: string): Promise<AgencyCompany> {
  return request(
    {
      url: COMPANIES_URL,
      method: 'POST',
      data: {
        company: {
          name,
        },
      },
    },
    (data) => {
      return data.model;
    }
  );
}

export function apiPublishCompany(id: string, signal?: AbortSignal): Promise<AppNS.Company> {
  return request(
    {
      url: `${COMPANIES}/${id}/publish.json`,
      method: 'PATCH',
      signal,
    },
    (data) => {
      return { ...data.model, documents: parseCompanyDocumentsData(data.model.documents) };
    }
  );
}

export function apiUpdateCompanyState(id: string, state: AppNS.CompanyState): Promise<AppNS.Company> {
  return request(
    {
      url: `${COMPANIES}/${id}/update-state.json`,
      method: 'PATCH',
      data: {
        company: {
          state,
        },
      },
    },
    (data) => {
      return data.model;
    }
  );
}

export function apiGetCompanyPublicProfile(id: string, signal?: AbortSignal): Promise<AppNS.Company> {
  return request(
    {
      url: `${COMPANIES}/${id}/profile.json`,
      method: 'GET',
      signal,
    },
    (data) => {
      return data.model;
    }
  );
}
